import { useCallback, useEffect, useState } from 'react';
import { Flag, UploadCloud } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { JobCard } from '@/components/jobs/JobCard';
import { cacheJobs, readCachedJobs } from '@/components/jobs/current-job';
import { useOfflineQueue } from '@/hooks/useOfflineQueue';
import { listCaptureJobs, listCaptures, type CaptureJob } from '@/lib/api';
import { useMyAccess } from '@/lib/access/access-context';
import { canCapture, CANNOT_CAPTURE_MESSAGE } from '@/lib/access/access-model';

/**
 * Home: My jobs (CAPTURE_JOBS.md section 6). The OPEN Capture Jobs the
 * operator is assigned to, from `GET /capture-jobs?status=OPEN`; a caller
 * holding `reviewCaptures` gets every OPEN job from the same call. Each card
 * names the job, the first line of its instructions and its capture count,
 * and opens the job view, which is the only way to a capture.
 *
 * Offline, the last list this device loaded stands in, dated, because an
 * operator in a basement aisle still needs to know what the job asks for.
 *
 * "Waiting on you" stays: the count still to upload is this device's own
 * queue, and the count sent back is `GET /capture?status=RECAPTURE_REQUESTED`.
 * A failed read leaves that row out rather than showing a zero it never
 * measured.
 */

type JobsState =
  | { kind: 'loading' }
  | { kind: 'loaded'; jobs: CaptureJob[] }
  | { kind: 'cached'; jobs: CaptureJob[]; savedAt: string }
  | { kind: 'failed' };

function savedAtLabel(savedAt: string): string {
  const date = new Date(savedAt);
  return Number.isNaN(date.getTime())
    ? 'earlier'
    : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function MyJobs() {
  const { access } = useMyAccess();
  const { queue } = useOfflineQueue();
  const [state, setState] = useState<JobsState>({ kind: 'loading' });
  const [recaptureCount, setRecaptureCount] = useState<number | null>(null);

  const load = useCallback((isLive: () => boolean) => {
    setState({ kind: 'loading' });
    listCaptureJobs({ status: 'OPEN' })
      .then((jobs) => {
        if (!isLive()) return;
        cacheJobs(jobs);
        setState({ kind: 'loaded', jobs });
      })
      .catch(() => {
        if (!isLive()) return;
        const cached = readCachedJobs();
        setState(cached ? { kind: 'cached', jobs: cached.jobs, savedAt: cached.savedAt } : { kind: 'failed' });
      });
  }, []);

  useEffect(() => {
    let live = true;
    load(() => live);
    listCaptures({ status: 'RECAPTURE_REQUESTED' })
      .then((page) => {
        if (live) setRecaptureCount(page.items.length);
      })
      .catch(() => {
        if (live) setRecaptureCount(null);
      });
    return () => {
      live = false;
    };
  }, [load]);

  const waitingToUpload = queue.filter((item) => item.status !== 'uploaded').length;
  const sentBack = recaptureCount ?? 0;
  const jobs = state.kind === 'loaded' || state.kind === 'cached' ? state.jobs : [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-base font-semibold">My jobs</h1>
        {canCapture(access) ? (
          <p className="text-sm text-muted">Pick a job to see what it asks for and add captures to it.</p>
        ) : (
          <p className="text-sm text-muted">{CANNOT_CAPTURE_MESSAGE}</p>
        )}
      </div>

      {(waitingToUpload > 0 || sentBack > 0) && (
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold text-muted">Waiting on you</h2>
          {waitingToUpload > 0 && (
            <Link
              to="/queue"
              className="inline-flex w-fit items-center gap-2 rounded-control py-2 text-sm text-accent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <UploadCloud className="size-4 shrink-0" />
              {waitingToUpload} {waitingToUpload === 1 ? 'capture' : 'captures'} still to upload
            </Link>
          )}
          {sentBack > 0 && (
            <Link
              to="/flagged"
              className="inline-flex w-fit items-center gap-2 rounded-control py-2 text-sm text-accent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <Flag className="size-4 shrink-0" />
              {sentBack} sent back for another photograph
            </Link>
          )}
        </section>
      )}

      <section className="flex flex-col gap-3" aria-label="Jobs">
        {state.kind === 'loading' && <p className="text-sm text-muted">Loading your jobs...</p>}

        {state.kind === 'cached' && (
          <p className="text-xs text-warning">
            Showing your jobs as they were at {savedAtLabel(state.savedAt)}. They could not be refreshed.
          </p>
        )}

        {state.kind === 'failed' && (
          <div className="flex flex-col items-start gap-3">
            <p className="text-sm text-warning">Your jobs could not be loaded. Check your connection and try again.</p>
            <Button variant="outline" onClick={() => load(() => true)}>
              Try again
            </Button>
          </div>
        )}

        {state.kind === 'loaded' && jobs.length === 0 && (
          <div className="flex flex-col gap-1 rounded-card border border-border p-4 sm:p-5">
            <p className="text-sm font-medium text-surface-foreground">No jobs are assigned to you.</p>
            <p className="text-sm text-muted">Ask your manager to assign you to a capture job, then open this screen again.</p>
          </div>
        )}

        {/* Two-up from the tablet breakpoint: a job card is short, and one
            per row wastes the width a tablet or monitor has. */}
        {jobs.length > 0 && (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {jobs.map((job) => (
              <li key={job.jobId} className="min-w-0">
                <JobCard job={job} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
